import { Box, Stack, Typography, Link as MuiLink } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useMemo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';
import { useEditContext, useGetList, useGetMany } from 'react-admin';

type ContactNavRecord = {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
};

const ContactPager = () => {
  const { record } = useEditContext<ContactNavRecord>();
  const location = useLocation();
  const locationState = location.state as
    | { contactIds?: number[]; hasFilters?: boolean }
    | undefined;
  const scopedContactIds = locationState?.contactIds;
  const contactIds = useMemo(
    () => (Array.isArray(scopedContactIds) ? scopedContactIds : []),
    [scopedContactIds]
  );
  const hasFilters = Boolean(locationState?.hasFilters);
  const hasScopedIds = contactIds.length > 0;

  const { data: scopedContacts = [] } = useGetMany<ContactNavRecord>('contacts', {
    ids: contactIds
  });
  const { data: contacts = [] } = useGetList<ContactNavRecord>('contacts', {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: 'lastName', order: 'ASC' }
  });
  // TODO: Replace with server-side paging/cursor when contacts grow beyond the in-memory limit.

  const orderedContacts = useMemo(() => {
    const source = hasScopedIds ? scopedContacts : contacts;
    const fallbackSorted = [...source].sort((a, b) => {
      const lastA = a.lastName ?? '';
      const lastB = b.lastName ?? '';
      const lastCompare = lastA.localeCompare(lastB);
      if (lastCompare !== 0) return lastCompare;
      const firstA = a.firstName ?? '';
      const firstB = b.firstName ?? '';
      const firstCompare = firstA.localeCompare(firstB);
      if (firstCompare !== 0) return firstCompare;
      return a.id - b.id;
    });
    if (!hasScopedIds) {
      return fallbackSorted;
    }

    const byId = new Map(scopedContacts.map((contact) => [contact.id, contact]));
    return contactIds
      .map((id) => byId.get(id))
      .filter((contact): contact is ContactNavRecord => Boolean(contact));
  }, [contacts, contactIds, hasScopedIds, scopedContacts]);

  const currentIndex = orderedContacts.findIndex((contact) => contact.id === record?.id);
  const prevContact = currentIndex > 0 ? orderedContacts[currentIndex - 1] : null;
  const nextContact =
    currentIndex >= 0 && currentIndex < orderedContacts.length - 1
      ? orderedContacts[currentIndex + 1]
      : null;

  const formatName = (contact: ContactNavRecord | null) => {
    if (!contact) return 'None';
    const parts = [contact.firstName, contact.lastName].filter(Boolean);
    return parts.length ? parts.join(' ') : `Contact ${contact.id}`;
  };

  if (!hasFilters || !hasScopedIds) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <MuiLink
            component={RouterLink}
            to={prevContact ? `/contacts/${prevContact.id}/show` : ''}
            state={hasScopedIds ? { contactIds, hasFilters } : undefined}
            underline="hover"
            color={prevContact ? 'primary' : 'text.disabled'}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              pointerEvents: prevContact ? 'auto' : 'none'
            }}
          >
            <ChevronLeftIcon fontSize="small" />
            <Typography variant="caption" color="inherit">
              Previous
            </Typography>
          </MuiLink>
          <Typography variant="caption" color="text.secondary">
            ({formatName(prevContact)})
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            ({formatName(nextContact)})
          </Typography>
          <MuiLink
            component={RouterLink}
            to={nextContact ? `/contacts/${nextContact.id}/show` : ''}
            state={hasScopedIds ? { contactIds, hasFilters } : undefined}
            underline="hover"
            color={nextContact ? 'primary' : 'text.disabled'}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              pointerEvents: nextContact ? 'auto' : 'none'
            }}
          >
            <Typography variant="caption" color="inherit">
              Next
            </Typography>
            <ChevronRightIcon fontSize="small" />
          </MuiLink>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ContactPager;
